// A user's progress on a deal (the server's Api::ActionSerializer). Re-exported from '@/types/deal'.
import type { ActionStatus } from './actionStatus';
import type { ShippingTracker, StepVideoExtras, VoltrActionDetails } from './socialDeal';

/**
 * Deal Steps v2. One provider confirmation of a step. `at` is when the
 * provider sent the postback (not when we processed it). `inferred` is true
 * when the step was confirmed by cascade from a later step's event (you
 * cannot have deposited without opening the account); false when the event
 * named this step directly.
 */
export interface StepConfirmation {
  at: string;
  event: string;
  inferred: boolean;
}

/** Deal Steps v2. The latest direct provider confirmation on an action. */
export interface LastProviderEvent {
  event: string;
  at: string;
  step_key: string;
  /** Server-resolved display name of that step; null if the key left the config. */
  step_name?: string | null;
}

/**
 * Deal Steps v2. The server-rendered visual state of one step (smart server,
 * dumb client): the app draws `state` and prints `label · relative(at)`
 * under a done step. Mirrors `Api::ActionSerializer::STATE_*`.
 */
export const SERVER_STEP_STATE = {
  Confirmed: 'confirmed',
  // The user said they did it, the provider has not confirmed yet (orange
  // clock, never a check). `completed` is the retired blue-check name, kept so
  // a cached payload from the short-lived earlier server still renders.
  Reported: 'reported',
  Overdue: 'overdue',
  Completed: 'completed',
  Active: 'active',
  Waiting: 'waiting',
  Locked: 'locked',
  Failed: 'failed',
} as const;
export type ServerStepState = (typeof SERVER_STEP_STATE)[keyof typeof SERVER_STEP_STATE];

export interface StepStateEntry extends StepVideoExtras {
  state: ServerStepState;
  /** Caption under the step ("Confirmed by SoFi", "Waiting for SoFi", "Confirmation overdue"). */
  label?: string;
  at?: string;
  /**
   * When present, `at` is not the pill's own time (reported/overdue: it is the
   * user's tap, "You marked it done"), so the pill prints `label · at_label relative(at)`
   * with the time segment in a lighter weight.
   */
  at_label?: string;
  /** Reported steps: when the provider's confirmation is expected by (ISO8601). */
  expected_by?: string;
  /** MarketPryce Social product step: the shipping tracker, built by the server. */
  tracker?: ShippingTracker;
  /**
   * MarketPryce Social: the server's words for this step right now, in place
   * of the step's own ("Applied" while a coach decides; "Video rejected" on a
   * reopened upload step).
   */
  name?: string;
  description?: string;
}

/**
 * Deal Steps v2. The card above the stepper: what the brand confirmed and
 * the user's one next action, computed by the server
 * (`Api::ActionSerializer.status_card`). Tones map to the pill tints.
 */
export interface StatusCard {
  tone: 'neutral' | 'success' | 'warning' | 'danger' | (string & {});
  title: string;
  body?: string | null;
  /** Overdue: a prefilled support email. `to` is the server's inbox; the app falls back to SUPPORT_EMAIL. */
  support_email?: { to?: string; subject: string; body: string } | null;
}

export interface Action {
  id: number;
  deal_id: number;
  deal_slug: string;
  status: ActionStatus;
  clicked_at: string | null;
  verified_at: string | null;
  rejected_at: string | null;
  expired_at: string | null;
  /** Which steps are DONE (user tap, provider cascade, or resync): key → ISO. */
  step_completions: Record<string, string>;
  active_step_key: string | null;
  /**
   * Deal Steps v2, additive: which steps the PROVIDER confirmed. Absent
   * against an older server. A key here renders green; a key only in
   * `step_completions` renders as the user's own blue mark.
   */
  step_confirmations?: Record<string, StepConfirmation>;
  /**
   * Deal Steps v2, additive: the server's rendering of every step in the
   * deal's current config. When present the stepper uses it verbatim and
   * derives nothing; absent against an older server (legacy derivation).
   */
  step_states?: Record<string, StepStateEntry>;
  /** Deal Steps v2, additive: the latest DIRECT provider confirmation. */
  last_provider_event?: LastProviderEvent | null;
  /** Deal Steps v2, additive: the card above the stepper. Absent on an older server. */
  status_card?: StatusCard | null;
  /** Deal Steps v2, additive: true when nothing about this action can change. */
  settled?: boolean;
  /** MarketPryce Social (Voltr) deals only: the order. Videos ride on `step_states` (StepVideo). */
  voltr?: VoltrActionDetails;
}
